<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/audioConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
  height: {
    type: Number,
    default: 100,
  },
  waveColor: {
    type: String,
    default: '',
  },
  progressColor: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
  'recordError',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const recordedAudioUrl = ref(null);
let isUnmounting = false;

const DEFAULT_WAVE_COLOR = '#1F93FF';
const DEFAULT_PROGRESS_COLOR = '#6E6F73';

const getBrandColorChannels = () => {
  if (typeof window === 'undefined') return null;

  const rawColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--brand-color')
    .trim();
  const channels = rawColor
    .split(/[\s,]+/)
    .map(Number)
    .filter(value => Number.isFinite(value))
    .map(value => Math.max(0, Math.min(255, value)));

  return channels.length === 3 ? channels : null;
};

const getWaveColors = () => {
  const brandChannels = getBrandColorChannels();
  const brandColor = brandChannels?.join(', ');

  return {
    waveColor:
      props.waveColor ||
      (brandColor ? `rgba(${brandColor}, 0.55)` : DEFAULT_WAVE_COLOR),
    progressColor:
      props.progressColor ||
      (brandColor ? `rgb(${brandColor})` : DEFAULT_PROGRESS_COLOR),
  };
};

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const AUDIO_EXTENSION_MAP = {
  'audio/ogg': 'ogg',
  'audio/mp3': 'mp3',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/webm': 'webm',
};

const getRecordPluginOptions = audioFormat => {
  const options = {
    scrollingWaveform: true,
    renderRecordedAudio: false,
  };
  if (
    audioFormat === 'audio/ogg' &&
    MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
  ) {
    options.mimeType = 'audio/ogg;codecs=opus';
  }
  return options;
};

const initWaveSurfer = () => {
  const { waveColor, progressColor } = getWaveColors();

  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor,
    progressColor,
    height: props.height,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create(getRecordPluginOptions(props.audioRecordFormat)),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    try {
      const audioBlob = await convertAudio(blob, props.audioRecordFormat);
      // Use the converted blob's actual type, which may differ from the
      // requested format when the browser can't produce it (e.g. Safari falls
      // back to MP3 instead of OGG). This keeps the filename, content type, and
      // voice-note flag consistent with the real bytes.
      const audioType = audioBlob.type || props.audioRecordFormat;
      const ext = AUDIO_EXTENSION_MAP[audioType] || 'mp3';
      const fileName = `${getUuid()}.${ext}`;
      const file = new File([audioBlob], fileName, {
        type: audioType,
      });
      if (recordedAudioUrl.value) URL.revokeObjectURL(recordedAudioUrl.value);
      recordedAudioUrl.value = URL.createObjectURL(audioBlob);
      wavesurfer.value.load(recordedAudioUrl.value).catch(error => {
        // WaveSurfer aborts an in-flight load when the recorder is removed.
        // That is expected while closing the composer/modal and must not become
        // an unhandled promise rejection in the browser.
        if (error?.name !== 'AbortError' && !isUnmounting) {
          emit('recordError', { error });
        }
      });
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
      hasRecording.value = true;
      isRecording.value = false;
    } catch (error) {
      isRecording.value = false;
      hasRecording.value = false;
      emit('recordError', { error });
    }
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = async () => {
  try {
    await record.value.startRecording();
    if (isUnmounting) {
      record.value?.stopRecording();
      return;
    }
    isRecording.value = true;
  } catch (error) {
    isRecording.value = false;
    emit('recordError', { error });
  }
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  isUnmounting = true;
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
    wavesurfer.value = null;
    record.value = null;
  }
  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value);
    recordedAudioUrl.value = null;
  }
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
